import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Trash2, Database, Calendar, HardDrive, RefreshCw, AlertCircle, Clock, AlertTriangle, Upload, RotateCcw, Image as ImageIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function BackupsManagement({ user, onLogout }) {
  const [backups, setBackups] = useState([]);
  const [uploadsArchives, setUploadsArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [creatingArchive, setCreatingArchive] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileInputRef = useRef(null);
  const uploadsFileInputRef = useRef(null);

  // Only main admin can access this page
  const isMainAdmin = user?.email === 'muneeb.mahadik@lioncroft.co.uk';

  // Count expiring soon backups
  const expiringCount = backups.filter(b => b.expiring_soon).length;

  useEffect(() => {
    if (isMainAdmin) {
      loadBackups();
      loadUploadsArchives();
    }
  }, [isMainAdmin]);

  const loadUploadsArchives = async () => {
    try {
      const res = await api.get('/uploads-archive');
      setUploadsArchives(res.data || []);
    } catch (e) {
      // Silent — endpoint may not exist on older deploys
      console.warn('Could not load uploads archives', e);
    }
  };

  const createUploadsArchive = async () => {
    try {
      setCreatingArchive(true);
      const res = await api.post('/uploads-archive/create');
      toast.success(`Archive created: ${res.data.file_count} files, ${res.data.size_mb} MB`);
      loadUploadsArchives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create uploads archive');
    } finally {
      setCreatingArchive(false);
    }
  };

  const regeneratePdfs = async (force = false) => {
    const msg = force
      ? `FORCE regenerate PDFs for ALL deliveries — this will overwrite every existing PDF. Continue?`
      : `Regenerate PDFs for deliveries with missing or broken PDF files. Continue?`;
    if (!confirm(msg)) return;
    try {
      setRestoring(true);
      const res = await api.post(`/deliveries/regenerate-pdfs?only_missing=${!force}`, null, { timeout: 900000 });
      const { regenerated, skipped_already_present, failed } = res.data;
      toast.success(`Regenerated ${regenerated} PDFs (skipped ${skipped_already_present}, failed ${failed})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF regeneration failed');
    } finally {
      setRestoring(false);
    }
  };

  const downloadUploadsArchive = async (filename) => {
    try {
      const res = await api.get(`/uploads-archive/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const deleteUploadsArchive = async (filename) => {
    if (!confirm(`Delete archive ${filename}?`)) return;
    try {
      await api.delete(`/uploads-archive/${filename}`);
      toast.success('Archive deleted');
      loadUploadsArchives();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const restoreUploadsFromUrl = async () => {
    const url = window.prompt('Paste a public URL to an uploads archive (.zip or .tar.gz):');
    if (!url || !url.trim()) return;
    const clearExisting = confirm('Clear existing uploads folder before extracting?\n\nOK = wipe & replace.\nCancel = merge (extracted files overwrite same-named files, but other files stay).');
    try {
      setRestoring(true);
      const res = await api.post('/uploads-archive/restore-url', { url: url.trim(), clear_existing: clearExisting }, { timeout: 900000 });
      toast.success(`Restored ${res.data.extracted_from_archive} files (uploads now has ${res.data.total_files_in_uploads} files)`);
      loadUploadsArchives();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Uploads URL restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const uploadAndRestoreUploads = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const okExt = ['.zip', '.tar', '.tar.gz', '.tgz'].some((e) => file.name.toLowerCase().endsWith(e));
    if (!okExt) {
      toast.error('Please select a .zip or .tar.gz archive');
      event.target.value = '';
      return;
    }
    const clearExisting = confirm('Clear existing uploads folder before extracting?\n\nOK = wipe & replace.\nCancel = merge (extracted files overwrite same-named files, but other files stay).');
    try {
      setRestoring(true);
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`/uploads-archive/restore-upload?clear_existing=${clearExisting}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 900000,
      });
      toast.success(`Restored ${res.data.extracted_from_archive} files (uploads now has ${res.data.total_files_in_uploads} files)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload & restore failed');
    } finally {
      setRestoring(false);
      event.target.value = '';
    }
  };

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/backups');
      setBackups(response.data);
    } catch (error) {
      toast.error('Failed to load backups');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setCreatingBackup(true);
      await api.post('/backups/create');
      toast.success('Backup created successfully!');
      loadBackups(); // Refresh list
    } catch (error) {
      toast.error('Failed to create backup');
      console.error(error);
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const response = await api.get(`/backups/${filename}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error('Failed to download backup');
      console.error(error);
    }
  };

  const deleteBackup = async (filename) => {
    if (!confirm(`Are you sure you want to delete backup: ${filename}?`)) {
      return;
    }

    try {
      await api.delete(`/backups/${filename}`);
      toast.success('Backup deleted successfully');
      loadBackups(); // Refresh list
    } catch (error) {
      toast.error('Failed to delete backup');
      console.error(error);
    }
  };

  const restoreFromFile = async (filename) => {
    const confirmMsg = `Restore from "${filename}"?\n\nThis will REPLACE all current data in:\nusers, customers, vehicles, deliveries, pallets, pallet_movements, notifications, load_photos, ocr_corrections.\n\nThis cannot be undone. Continue?`;
    if (!confirm(confirmMsg)) return;
    try {
      setRestoring(true);
      const res = await api.post(`/backups/restore/${filename}`);
      const total = res.data?.total_docs ?? 0;
      toast.success(`Restored ${total.toLocaleString()} documents from ${filename}`);
      loadBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
      console.error(error);
    } finally {
      setRestoring(false);
    }
  };

  const uploadAndRestore = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a .json backup file');
      return;
    }
    const confirmMsg = `Upload "${file.name}" (${(file.size / 1024 / 1024).toFixed(2)} MB) and restore?\n\nThis will REPLACE all current data in:\nusers, customers, vehicles, deliveries, pallets, pallet_movements, notifications, load_photos, ocr_corrections.\n\nThis cannot be undone. Continue?`;
    if (!confirm(confirmMsg)) {
      event.target.value = '';
      return;
    }
    try {
      setRestoring(true);
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/backups/restore-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 600000, // 10 minutes for large restores
      });
      const total = res.data?.total_docs ?? 0;
      toast.success(`Restored ${total.toLocaleString()} documents from ${file.name}`);
      loadBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload & restore failed');
      console.error(error);
    } finally {
      setRestoring(false);
      event.target.value = '';
    }
  };

  const restoreFromUrl = async () => {
    const url = window.prompt(
      'Paste the public URL to a backup JSON file.\n\n(Use this when the upload-restore fails due to proxy/ingress size limits — the server downloads the file directly.)'
    );
    if (!url || !url.trim()) return;
    const confirmMsg = `Fetch backup from URL and restore?\n\nURL: ${url}\n\nThis will REPLACE all current data in:\nusers, customers, vehicles, deliveries, pallets, pallet_movements, notifications, load_photos, ocr_corrections.\n\nThis cannot be undone. Continue?`;
    if (!confirm(confirmMsg)) return;
    try {
      setRestoring(true);
      const res = await api.post('/backups/restore-url', { url: url.trim() }, { timeout: 600000 });
      const total = res.data?.total_docs ?? 0;
      const size = res.data?.size_mb;
      toast.success(`Restored ${total.toLocaleString()} documents${size ? ` (${size} MB)` : ''} from URL`);
      loadBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'URL restore failed');
      console.error(error);
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExpiryBadge = (backup) => {
    if (!backup.days_until_expiry && backup.days_until_expiry !== 0) return null;
    
    if (backup.expiring_soon) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <AlertTriangle className="h-3 w-3" />
          Expires in {backup.days_until_expiry} days
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
        <Clock className="h-3 w-3" />
        {backup.days_until_expiry} days left
      </span>
    );
  };

  // Redirect if not main admin
  if (!isMainAdmin) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Only the main administrator can access backup management.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Database Backups</h1>
            <p className="text-slate-600 mt-2">Manage your automated and manual database backups</p>
          </div>
          <div className="flex gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={uploadAndRestore}
              data-testid="restore-file-input"
            />
            <Button
              onClick={restoreFromUrl}
              variant="outline"
              disabled={restoring || creatingBackup}
              data-testid="restore-from-url-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Restore from URL
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              disabled={restoring || creatingBackup}
              data-testid="upload-restore-btn"
            >
              {restoring ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload &amp; Restore
                </>
              )}
            </Button>
            <Button
              onClick={loadBackups}
              variant="outline"
              disabled={loading}
              data-testid="refresh-backups-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={createBackup}
              disabled={creatingBackup}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              data-testid="create-backup-btn"
            >
              {creatingBackup ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Create Backup Now
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Expiring Soon Warning */}
        {expiringCount > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-orange-500 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-orange-800">
                {expiringCount} backup{expiringCount > 1 ? 's' : ''} expiring soon
              </h4>
              <p className="text-sm text-orange-700">
                Download these backups before they are automatically deleted after 60 days.
              </p>
            </div>
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Backups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{backups.length}</div>
              <p className="text-xs text-gray-500 mt-1">Available backup files</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Latest Backup</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-gray-900">
                {backups[0] ? formatDate(backups[0].created_at) : 'No backups yet'}
              </div>
              <p className="text-xs text-gray-500 mt-1">Most recent backup</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-green-600">Daily at 6:00 PM</div>
              <p className="text-xs text-gray-500 mt-1">UK GMT time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Retention</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-purple-600">60 Days</div>
              <p className="text-xs text-gray-500 mt-1">Auto-delete after expiry</p>
            </CardContent>
          </Card>
        </div>

        {/* Backups List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Backup Archive
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading backups...</p>
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-12">
                <Database className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Backups Yet</h3>
                <p className="text-gray-600 mb-4">Create your first backup to get started</p>
                <Button onClick={createBackup} disabled={creatingBackup}>
                  <Database className="h-4 w-4 mr-2" />
                  Create First Backup
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div
                    key={backup.filename}
                    className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                      backup.expiring_soon 
                        ? 'bg-orange-50 border border-orange-200 hover:bg-orange-100' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Database className={`h-5 w-5 ${backup.expiring_soon ? 'text-orange-500' : 'text-blue-500'}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{backup.filename}</h3>
                            {getExpiryBadge(backup)}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Created: {formatDate(backup.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <HardDrive className="h-3 w-3" />
                              {backup.size_mb} MB
                            </span>
                            {backup.expires_at && (
                              <span className="flex items-center gap-1 text-gray-500">
                                <Clock className="h-3 w-3" />
                                Expires: {formatDate(backup.expires_at)}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-3 mt-2 text-xs text-gray-500">
                            {backup.collections_count && Object.entries(backup.collections_count).map(([collection, count]) => (
                              count > 0 && (
                                <span key={collection} className="bg-white px-2 py-1 rounded">
                                  {collection}: {count}
                                </span>
                              )
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => downloadBackup(backup.filename)}
                        variant={backup.expiring_soon ? "default" : "outline"}
                        size="sm"
                        className={backup.expiring_soon ? "bg-orange-500 hover:bg-orange-600" : ""}
                        data-testid={`download-backup-${backup.filename}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {backup.expiring_soon ? "Download Now" : "Download"}
                      </Button>
                      <Button
                        onClick={() => restoreFromFile(backup.filename)}
                        variant="outline"
                        size="sm"
                        disabled={restoring}
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        data-testid={`restore-backup-${backup.filename}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                      <Button
                        onClick={() => deleteBackup(backup.filename)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-backup-${backup.filename}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos & PDFs Archive Section */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-purple-600" />
                Photos &amp; PDFs Archive
              </CardTitle>
              <div className="flex gap-2">
                <input
                  ref={uploadsFileInputRef}
                  type="file"
                  accept=".zip,.tar,.tar.gz,.tgz,application/zip,application/x-tar,application/gzip"
                  className="hidden"
                  onChange={uploadAndRestoreUploads}
                  data-testid="restore-uploads-file-input"
                />
                <Button
                  onClick={restoreUploadsFromUrl}
                  variant="outline"
                  size="sm"
                  disabled={restoring}
                  data-testid="restore-uploads-url-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Restore from URL
                </Button>
                <Button
                  onClick={() => uploadsFileInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                  disabled={restoring}
                  data-testid="upload-restore-uploads-btn"
                >
                  {restoring ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Upload &amp; Restore
                </Button>
                <Button
                  onClick={() => regeneratePdfs(false)}
                  variant="outline"
                  size="sm"
                  disabled={restoring}
                  data-testid="regenerate-pdfs-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Regenerate Missing PDFs
                </Button>
                <Button
                  onClick={() => regeneratePdfs(true)}
                  variant="outline"
                  size="sm"
                  disabled={restoring}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  data-testid="regenerate-pdfs-force-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Force Regenerate ALL
                </Button>
                <Button
                  onClick={createUploadsArchive}
                  size="sm"
                  disabled={creatingArchive}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid="create-uploads-archive-btn"
                >
                  {creatingArchive ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                  Archive Photos &amp; PDFs
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Database backups <strong>do not include photos or PDFs</strong> — those files live on the server&apos;s disk.
              Use this section to bundle the entire <code>/uploads</code> folder into a downloadable zip,
              then re-upload it after redeployment so historical delivery photos and PDFs work again.
            </p>
            {uploadsArchives.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4 border border-dashed rounded">
                No photo/PDF archives yet. Click <strong>&quot;Archive Photos &amp; PDFs&quot;</strong> to create one.
              </div>
            ) : (
              <div className="space-y-2">
                {uploadsArchives.map((a) => (
                  <div key={a.filename} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded">
                    <div className="flex items-center gap-3 text-sm">
                      <ImageIcon className="h-4 w-4 text-purple-500" />
                      <span className="font-mono">{a.filename}</span>
                      <span className="text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                      <span className="text-slate-500">{a.size_mb} MB</span>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => downloadUploadsArchive(a.filename)} variant="outline" size="sm" data-testid={`download-archive-${a.filename}`}>
                        <Download className="h-4 w-4 mr-2" />Download
                      </Button>
                      <Button onClick={() => deleteUploadsArchive(a.filename)} variant="outline" size="sm" className="text-red-600 hover:bg-red-50" data-testid={`delete-archive-${a.filename}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Backup Information</h4>
          <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
            <li>Backups are created automatically every day at <strong>6:00 PM UK GMT</strong></li>
            <li>Backups are automatically deleted after <strong>60 days</strong></li>
            <li>You will receive <strong>notifications 2 weeks before</strong> a backup expires</li>
            <li>You can manually create a backup anytime using the &quot;Create Backup Now&quot; button</li>
            <li>Backup files contain all customers, deliveries, pallets, users, and notifications</li>
            <li>Download backups to restore data or migrate to another system</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
